/**
 * CodeEditor — sl-e0il.1.2
 *
 * CodeMirror-6-backed code editor with language-aware syntax
 * highlighting (JavaScript, Python, JSON). Exposes `EditorAdapter`
 * via `forwardRef` so `<TemplateInsertMenu>` and autocomplete wiring
 * can insert text at the cursor without knowing the underlying editor.
 *
 * Consumers use this via the `<LazyCodeEditor>` export (lazy-loaded
 * to keep CodeMirror out of the initial workflow-builder bundle).
 */

import { css } from '@codemirror/lang-css';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { python } from '@codemirror/lang-python';
import type { Extension } from '@codemirror/state';
import { EditorView, tooltips } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import * as styles from './editor.css';
import type { EditorAdapter } from './editor-adapter';
import {
  createCodeMirrorAdapter,
  useEchoSuppressedValue,
} from './internal/codemirror-helpers';
import { pasteDedentExtension } from './internal/paste-dedent';
import { syntaxLinterExtension } from './internal/syntax-linter';

export type CodeEditorLanguage =
  | 'javascript'
  | 'python'
  | 'json'
  | 'css'
  | 'text';

export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: CodeEditorLanguage;
  readOnly?: boolean;
  placeholder?: string;
  onBlur?: () => void;
  /** Extra CodeMirror extensions (autocomplete, decorations, etc). */
  extensions?: readonly Extension[];
  /** Accessible name applied to CodeMirror's real contenteditable surface. */
  ariaLabel?: string;
  /** Editor height — defaults to `auto` to size with content. */
  height?: string;
  /**
   * Fill the parent flex row instead of the default 80-600px content
   * sizing. Used by the fullscreen dialog. Automatically passes
   * `height="100%"` to CodeMirror and applies the `editorRootFill`
   * modifier class.
   */
  fill?: boolean;
  /**
   * Host element for the CodeMirror tooltip portal. Default
   * (`undefined`) → `document.body`, which escapes the bottom dock's
   * `backdrop-filter` containing block. The fullscreen dialog passes a
   * dedicated absolute-positioned host inside the Modal tree so
   * tooltips aren't swallowed by Radix's `aria-hidden` / `inert` state
   * on body-siblings. Must NEVER be a flex container — CM appends a
   * `position: relative` Ghost child that would become a flex-item and
   * break the parent's layout.
   */
  tooltipsParent?: HTMLElement | null;
}

function languageExtension(lang: CodeEditorLanguage): Extension {
  switch (lang) {
    case 'javascript':
      return javascript({ jsx: false, typescript: false });
    case 'python':
      return python();
    case 'json':
      return json();
    case 'css':
      return css();
    case 'text':
      return [];
  }
}

export const CodeEditor = forwardRef<EditorAdapter, CodeEditorProps>(
  function CodeEditor(props, ref) {
    const viewRef = useRef<import('@codemirror/view').EditorView | null>(null);

    useImperativeHandle(
      ref,
      () =>
        createCodeMirrorAdapter({
          get current() {
            return viewRef.current;
          },
        }),
      []
    );

    // Race guard — see `useEchoSuppressedValue` comment in
    // `internal/codemirror-helpers.ts`. Fast typists otherwise see the
    // editor snap back to a stale value mid-edit because panel commits
    // go through an async Y.js chain.
    const { value: guardedValue, onChange: guardedOnChange } =
      useEchoSuppressedValue(props.value, props.onChange);

    // Tooltip portal: default to `document.body` (escapes bottom-dock
    // `backdrop-filter`). Fullscreen dialog passes an absolute-
    // positioned host inside the Modal tree so tooltips aren't on an
    // aria-hidden / inert body-sibling. Long-form rationale lives in
    // `template-editor.tsx` (and the memory note on tooltip Ghosts).
    const tooltipsParent = props.tooltipsParent;
    const extensions = useMemo(
      () => [
        tooltips({ parent: tooltipsParent ?? document.body }),
        languageExtension(props.language),
        pasteDedentExtension(),
        // sl-2q8e.6.1: CSS grammar is covered by lang-css; the Lezer-based
        // syntaxLinter only ships rules for javascript/python/json.
        ...(props.language === 'css'
          ? []
          : props.language === 'text'
            ? []
            : [syntaxLinterExtension(props.language)]),
        ...(props.ariaLabel
          ? [
              EditorView.contentAttributes.of({
                'aria-label': props.ariaLabel,
              }),
            ]
          : []),
        ...(props.extensions ?? []),
      ],
      [tooltipsParent, props.language, props.ariaLabel, props.extensions]
    );

    const rootClassName = [
      styles.editorRoot,
      props.fill ? styles.editorRootFill : '',
      props.readOnly ? styles.editorReadOnly : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={rootClassName}>
        <CodeMirror
          value={guardedValue}
          onChange={guardedOnChange}
          extensions={extensions}
          readOnly={props.readOnly}
          placeholder={props.placeholder}
          height={props.fill ? '100%' : (props.height ?? 'auto')}
          aria-label={props.ariaLabel}
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            highlightActiveLine: !props.readOnly,
            highlightActiveLineGutter: false,
            bracketMatching: props.language !== 'text',
            closeBrackets: props.language !== 'text',
            autocompletion: false,
            indentOnInput: props.language !== 'text',
          }}
          onCreateEditor={view => {
            viewRef.current = view;
          }}
          onBlur={() => props.onBlur?.()}
        />
      </div>
    );
  }
);
